import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MUSIC_PROMPT_SUGGESTIONS, BORDER_RADIUS } from '@/constants/config';
import PropTypes from 'prop-types';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  grid: {
    ...(Platform.OS === 'web' ? {
      display: 'grid',
      gridTemplateColumns: isXS ? '1fr' : 'repeat(2, 1fr)',
      gap: 12,
    } : {
      flexDirection: isXS ? 'column' : 'row',
      flexWrap: 'wrap',
      gap: 12,
    }),
  },
  gridItem: {
    ...(Platform.OS !== 'web' && !isXS ? { width: '48.5%' } : {}),
  },
});

const promptItemStyles = (colors, isHovered, isAltBGColor) => StyleSheet.create({
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: isHovered ? (isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary) : (isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block),
    borderRadius: BORDER_RADIUS.medium || 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 12,
    ...(Platform.OS === 'web' && { transitionDuration: '150ms' }),
  },
  thumbnail: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
  },
  textContainer: {
    flex: 1,
    minWidth: 0, // Required on RN so flex row child gets width; without this text can collapse to 0
  },
  promptText: {
    color: colors.text_color_primary,
  },
});

const MusicPromptItem = React.memo(({ prompt, onPress }) => {
  const { colors, theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  
  const styles = useMemo(() => promptItemStyles(colors, isHovered, isAltBGColor), [colors, isHovered, isAltBGColor]);

  return (
    <Pressable
      style={styles.item}
      onPress={() => onPress(prompt)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      {(prompt.previewImageCdn || prompt.thumbnail) ? (
        <Image
          source={{ uri: prompt.previewImageCdn || prompt.thumbnail }}
          style={[styles.thumbnail, { backgroundColor: colors.solid_color_tertiary }]}
          contentFit="cover"
          cachePolicy="memory-disk"
          priority="high"
          transition={150}
        />
      ) : (
        <View style={styles.thumbnail} />
      )}
      <View style={styles.textContainer}>
        <Text size="s" color="primary" numberOfLines={2} style={styles.promptText}>
          {prompt.text}
        </Text>
      </View>
    </Pressable>
  );
});

const MusicPromptGrid = ({ onPromptSelect }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const handlePromptPress = useCallback((prompt) => {
    // Open the lyrics modal with this prompt
    if (onPromptSelect && prompt) {
      onPromptSelect(prompt);
    }
  }, [onPromptSelect]);

  return (
    <View style={styles.container}>
      <View style={styles.grid}>
        {MUSIC_PROMPT_SUGGESTIONS.map((prompt) => (
          <View key={prompt.id} style={styles.gridItem}>
            <MusicPromptItem
              prompt={prompt}
              onPress={handlePromptPress}
            />
          </View>
        ))}
      </View>
    </View>
  );
};

MusicPromptGrid.propTypes = {
  onPromptSelect: PropTypes.func.isRequired,
};

export default React.memo(MusicPromptGrid);

